import Button from "@ch/components/Buttons/Button";
import NavLinkButton from "@ch/components/Links/NavLinkButton";
import type {
  CoachWithRelations,
  UserSubscriptionsWithRelations,
} from "@ch/generated";
import { openLogoutModal } from "@ch/helpers/utils/helpers";
import postHogFeatureFlagKeys from "@ch/helpers/utils/postHogAnalytics/postHogFeatureFlagKeys.ts";
import usePostHogFeatureFlag from "@ch/helpers/utils/postHogAnalytics/usePostHogFeatureFlag.ts";
import useGetReferrals from "@ch/pages/ChatPage/hooks/useGetReferrals.ts";
import useGetUserSubscriptions from "@ch/pages/SubscriptionsPage/hooks/useGetUserSubscriptions.ts";
import { addCoachIdsWithActiveUserSubscription } from "@ch/redux-store/chat/actions.ts";
import { useAppSelector } from "@ch/redux-store/hooks/useAppSelector.ts";
import { AppointmentsRoutesEnum } from "@ch/router/ Appointments/enums";
import { AppRoutesEnum } from "@ch/router/AppRoutes/enums";
import { ChatRoutesEnum } from "@ch/router/ChatRoutes/enums";
import { ForumsRoutesEnum } from "@ch/router/Forums/enums";
import { HealthDiaryRoutesEnum } from "@ch/router/HealthDiaryRoutes/enums";
import { HealthPassportRoutesEnum } from "@ch/router/HealthPassportRoutes/enums";
import { MindfulnessRoutesEnum } from "@ch/router/MindfulnessRoutes/enums";
import { NaviHealthRoutesEnum } from "@ch/router/NaviHealthRoutes/enums";
import moment from "moment/moment";
import { type FC, useEffect, useMemo } from "react";
import { useDispatch } from "react-redux";

interface SidebarRoutesProps {
  isClosed: boolean;
}

const SidebarRoutes: FC<SidebarRoutesProps> = ({ isClosed }) => {
  const { isFeatureFlagEnabled } = usePostHogFeatureFlag({
    featureFlagName: postHogFeatureFlagKeys.DISABLE_CHATS_AND_FORUMS,
  });
  const dispatch = useDispatch();
  const unreadMessages =
    useAppSelector((state) => state.chat?.unreadMessages) ?? [];
  const { referralCoaches } = useGetReferrals();
  const { subscriptions: allSubs } = useGetUserSubscriptions();
  const subscriptions = useMemo(
    () =>
      allSubs.filter((item) => moment(item.endDate).isAfter(moment(), "day")),
    [allSubs],
  );
  const filteredCoaches = useMemo(
    () =>
      subscriptions
        ?.filter((item) => moment(item.endDate).isAfter(moment()))
        ?.map((item: UserSubscriptionsWithRelations) => item?.coach?.coach)
        .filter((item: CoachWithRelations | undefined) => !!item)
        .reduce(
          (t: CoachWithRelations[], a: CoachWithRelations | undefined) => {
            if (!t.find((x) => x.id === a?.id)) {
              t.push(a!);
            }
            return t;
          },
          [],
        ),
    [subscriptions],
  );

  const hasActiveSubscription = (subscriptions?.length ?? 0) > 0;

  // Ids of the coaches with user active subscription
  const filteredCoachIds = useMemo(() => {
    return [
      ...filteredCoaches.map((coach) => coach?.id),
      ...(hasActiveSubscription ? referralCoaches : []).map(
        (coach) => coach?.id,
      ),
    ];
  }, [filteredCoaches, hasActiveSubscription, referralCoaches]);

  useEffect(() => {
    if (filteredCoachIds?.length) {
      dispatch(
        addCoachIdsWithActiveUserSubscription({
          coachIdsWithActiveUserSubscription: filteredCoachIds as string[],
        }),
      );
    }
  }, [dispatch, filteredCoachIds]);

  const unreadMessagesFilteredBuActiveSubscription = unreadMessages.filter(
    (message) => filteredCoachIds?.includes(message?.senderCoachId),
  );

  return (
    <>
      <NavLinkButton
        iconOnly={isClosed}
        leftIcon="category"
        to={AppRoutesEnum.ROOT}
      >
        Dashboard
      </NavLinkButton>
      <NavLinkButton
        iconOnly={isClosed}
        leftIcon="health-passport"
        to={HealthPassportRoutesEnum.ROOT}
      >
        Health Passport
      </NavLinkButton>
      <NavLinkButton
        iconOnly={isClosed}
        leftIcon="calendar-tick"
        to={AppointmentsRoutesEnum.ROOT}
      >
        Appointments
      </NavLinkButton>
      <NavLinkButton
        iconOnly={isClosed}
        leftIcon="chat"
        style={
          isFeatureFlagEnabled ? { pointerEvents: "none", opacity: 0.5 } : {}
        }
        to={ChatRoutesEnum.ROOT}
      >
        <div className="flex items-center gap-2">
          Chat
          <div className="flex flex-col items-end">
            {unreadMessagesFilteredBuActiveSubscription?.length > 0 && (
              <div className="flex h-6 w-6 items-center justify-center rounded-full bg-orange text-center">
                <p className="text-xs text-white">
                  {unreadMessagesFilteredBuActiveSubscription?.length}
                </p>
              </div>
            )}
          </div>
        </div>
      </NavLinkButton>
      <NavLinkButton
        iconOnly={isClosed}
        leftIcon="forum"
        style={
          isFeatureFlagEnabled ? { pointerEvents: "none", opacity: 0.5 } : {}
        }
        to={ForumsRoutesEnum.ROOT}
      >
        Forums
      </NavLinkButton>
      <NavLinkButton
        iconOnly={isClosed}
        leftIcon="kem-chat"
        to={AppRoutesEnum.KEM}
      >
        <div
          className="tooltip tooltip-top tooltip-secondary flex items-start before:ml-5 before:max-w-[200px] before:whitespace-normal before:text-center"
          data-tip="Meet Kem! Our new chatbot coach team member"
        >
          Kem
          <sup className="ml-2 mt-1 rounded-full bg-orange p-[6px] text-[7px] font-bold text-white">
            NEW
          </sup>
        </div>
      </NavLinkButton>

      <NavLinkButton
        iconOnly={isClosed}
        leftIcon="health-diary"
        to={HealthDiaryRoutesEnum.ROOT}
      >
        Health Diary
      </NavLinkButton>
      {(import.meta.env.DEV || import.meta.env.STAGING) && (
        <>
          <NavLinkButton
            iconOnly={isClosed}
            leftIcon="navigation"
            to={NaviHealthRoutesEnum.ROOT}
          >
            NaviHealth
          </NavLinkButton>
          <NavLinkButton
            iconOnly={isClosed}
            leftIcon="bell"
            to={AppRoutesEnum.NOTIFICATIONS}
          >
            Notifications
          </NavLinkButton>
        </>
      )}
      <NavLinkButton
        iconOnly={isClosed}
        leftIcon="subscription"
        to={AppRoutesEnum.SUBSCRIPTIONS}
      >
        My Programs
      </NavLinkButton>
      <NavLinkButton
        iconOnly={isClosed}
        leftIcon="mindfulness"
        to={MindfulnessRoutesEnum.ROOT}
      >
        Mindfulness
      </NavLinkButton>
      <NavLinkButton
        iconOnly={isClosed}
        leftIcon="radar"
        to={AppRoutesEnum.SYMPTOM_TRACKER}
      >
        Symptom Tracker
      </NavLinkButton>

      {/* Divider */}
      <div className="border-b border-b-gray-200" />

      <NavLinkButton
        iconOnly={isClosed}
        leftIcon="menu"
        to={AppRoutesEnum.MENU}
      >
        Menu
      </NavLinkButton>
      <Button
        className="!justify-start !gap-3 !text-black-700"
        intent="ghost"
        leftIcon="login"
        onClick={openLogoutModal}
        shape={isClosed ? "icon" : "rounded"}
        title={isClosed ? "Log Out" : undefined}
      >
        {!isClosed && "Log Out"}
      </Button>

      <footer className="py-4 text-center text-xs text-gray">
        © 2023 mDoc. All rights reserved.
      </footer>

      {/* Icons for Dev only */}
      {import.meta.env.DEV && (
        <NavLinkButton
          iconOnly={isClosed}
          leftIcon="heartbeat"
          to={AppRoutesEnum.COMPONENTS_LIBRARY}
        >
          Components Library
        </NavLinkButton>
      )}
    </>
  );
};

export default SidebarRoutes;
